// src/app/shared/search.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { StrapiService } from './strapi.service';
import { SiteService } from './site.service';
import { HtmlSanitizerUtil } from '../utils/htmlSanitizer.utils';
import { APIDomainVersion, ContentType } from '../models/site.model';
import {
  SearchResultsResponse,
  PaginationMeta,
  TypeFilterCounts,
} from '../models/search.model';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private currentDomainVersion: APIDomainVersion;
  contentType: ContentType = 'api';

  // Snippet configuration
  private readonly SNIPPET_CONTEXT_CHARS = 60; // Characters before/after match
  private readonly MAX_SNIPPETS = 3; // Maximum number of excerpts to show

  constructor(
    private strapiService: StrapiService,
    private siteService: SiteService,
    private router: Router,
  ) {
    this.siteService.currentDomainVersion$.subscribe((currentDomainVersion) => {
      this.currentDomainVersion = currentDomainVersion;
      this.getGroups(currentDomainVersion);
    });

    this.siteService.contentType$.subscribe((contentType) => {
      this.contentType = contentType;
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.resetSearch());
  }

  private allTypeFilters: (keyof TypeFilterCounts)[] = [
    'Endpoint',
    'Parameter',
    'Response',
    'Response Property',
    'Docs Page',
  ];

  // Pagination state
  private paginationSubject = new BehaviorSubject<PaginationMeta>({
    page: 1,
    pageSize: 10,
    pageCount: 0,
    total: 0,
  });
  pagination$ = this.paginationSubject.asObservable();

  private isSearchingSubject = new BehaviorSubject<boolean>(false);
  isSearching$ = this.isSearchingSubject.asObservable();

  private searchQuerySourceSubject = new BehaviorSubject<string>('');
  searchQuery$ = this.searchQuerySourceSubject.asObservable();

  private selectedGroupFilterSourceSubject = new BehaviorSubject<Array<any>>([]);
  groupFilter$ = this.selectedGroupFilterSourceSubject.asObservable();

  private selectedTypeFilterSourceSubject = new BehaviorSubject<Array<any>>([]);
  typeFilter$ = this.selectedTypeFilterSourceSubject.asObservable();

  private groupFiltersWithCountSourceSubject = new BehaviorSubject<Array<any>>([]);
  groupFiltersWithCount$ = this.groupFiltersWithCountSourceSubject.asObservable();

  private typeFiltersWithCountSourceSubject = new BehaviorSubject<Array<any>>([]);
  typeFiltersWithCount$ = this.typeFiltersWithCountSourceSubject.asObservable();

  private resultsSourceSubject = new BehaviorSubject<Array<any>>([]);
  searchResults$ = this.resultsSourceSubject.asObservable();

  private groupsSource = new BehaviorSubject<Array<any>>([]);
  allGroupFilters$ = this.groupsSource.asObservable();

  private loadingSource = new BehaviorSubject<boolean>(false);
  searchLoading$ = this.loadingSource.asObservable();

  /**
   * Setter method to set searching state
   * 
   * @param isSearching - whether a search is in progress
   */
  setSearching(isSearching: boolean) {
    this.isSearchingSubject.next(isSearching);
  }
  
  /**
   * Setter method to set search query. Also triggers a reset on all filters and pagination.
   * 
   * @param query - search query string
   */
  setSearchQuery(query: string) {
    this.clearFilters();
    this.resetPagination();
    this.searchQuerySourceSubject.next(query);
    this.setSearching(query.length > 0);
    this.updateSearchResults(true);
  }

  /**
   * Setter method to set selected group filters. Also triggers a reset on pagination and updates results.
   * 
   * @param filterValue - the group filter value to set
   * @param isChecked - whether the filter is checked or not
   */
  setSelectedGroupFilter(filterValue: any, isChecked: boolean) {
    let groupFilters = this.selectedGroupFilterSourceSubject.getValue();
    if (isChecked) {
      if (!groupFilters.includes(filterValue))
        groupFilters.push(filterValue);
    } else {
      const index = groupFilters.indexOf(filterValue);
      if (index !== -1) {
        groupFilters.splice(index, 1);
      }
    }

    this.selectedGroupFilterSourceSubject.next(groupFilters);
    this.resetPagination();
    this.updateSearchResults(false);
  }

  /**
   * Setter method to set selected type filters. Also triggers a reset on pagination and updates results.
   * 
   * @param filterValue - the type filter value to set
   * @param isChecked - whether the filter is checked or not
   */

  setSelectedTypeFilter(filterValue: any, isChecked: boolean) {
    let typeFilters = this.selectedTypeFilterSourceSubject.getValue();
    if (isChecked) {
      if (!typeFilters.includes(filterValue))
        typeFilters.push(filterValue);
    } else {
      const index = typeFilters.indexOf(filterValue);
      if (index !== -1) {
        typeFilters.splice(index, 1);
      }
    }

    this.selectedTypeFilterSourceSubject.next(typeFilters);
    this.resetPagination();
    this.updateSearchResults(false);
  }

  /**
   * Setter method to set search loading state
   * 
   * @param state - whether search is loading or not
   */
  setSearchLoading(state: boolean) {
    this.loadingSource.next(state);
  }

  // Pagination methods

  /**
   * Method to go to a specific page
   * 
   * @param page - the page number to go to
   */
  goToPage(page: number) {
    const currentPagination = this.paginationSubject.getValue();
    if (page >= 1 && page <= currentPagination.pageCount) {
      this.paginationSubject.next({ ...currentPagination, page });
      this.updateSearchResults(false);
    }
  }

  /**
   * Helper to go to the next page if possible
   */
  nextPage() {
    const { page, pageCount } = this.paginationSubject.getValue();
    if (page < pageCount) {
      this.goToPage(page + 1);
    }
  }

  /**
   * Helper to go to the previous page if possible
   */
  prevPage() {
    const { page } = this.paginationSubject.getValue();
    if (page > 1) {
      this.goToPage(page - 1);
    }
  }

  /**
   * Setter method to set page size. Also resets to first page and updates results.
   * 
   * @param pageSize - the page size to set
   */
  setPageSize(pageSize: number) {
    const currentPagination = this.paginationSubject.getValue();
    this.paginationSubject.next({ ...currentPagination, page: 1, pageSize });
    this.updateSearchResults(false);
  }

  /**
   * Re-runs search results with the current domain version and filters.
   * Call after changing the domain version via siteService so results reflect the new domain.
   */
  refreshResults() {
    this.resetPagination();
    this.updateSearchResults(true);
  }

  /**
   * Resets pagination to first page with zeroed counts
   */
  private resetPagination() {
    const currentPagination = this.paginationSubject.getValue();
    this.paginationSubject.next({
      ...currentPagination,
      page: 1,
      pageCount: 0,
      total: 0,
    });
  }

  /**
   * Main method to fetch and update search results
   * 
   * @param updateFilterCounts - deteremines whether to also update the filters
   * @returns 
   */
  updateSearchResults(updateFilterCounts: boolean) {
    const searchQuery = this.searchQuerySourceSubject.getValue();

    // Don't search if there's no query
    if (!searchQuery || searchQuery.trim() === '') {
      this.resultsSourceSubject.next([]);
      this.loadingSource.next(false);
      return;
    }

    this.setSearchLoading(true);
    const groupFilter = this.selectedGroupFilterSourceSubject.getValue();
    const typeFilter = this.selectedTypeFilterSourceSubject.getValue();
    const { page, pageSize } = this.paginationSubject.getValue();

    this.strapiService
      .getSearchResults(
        this.currentDomainVersion,
        searchQuery,
        groupFilter.length ? groupFilter : [],
        typeFilter.length ? typeFilter : [],
        page,
        pageSize,
        this.contentType
      )
      .subscribe((response: SearchResultsResponse) => {
        const { data, meta } = response;

        // Update pagination metadata
        this.paginationSubject.next(meta.pagination);

        if (data.length > 0) {
          const results = (data ?? []).map((result: any) => {
            // Strip HTML and create snippet for description
            const plainDescription = HtmlSanitizerUtil.stripHtmlWithSpaces(result.description);
            const descriptionSnippet = this.createSearchSnippet(searchQuery, plainDescription);

            // Base fields for all result types
            const baseResult = {
              label: this.highlightSearchTerm(searchQuery, result.label),
              description: descriptionSnippet,
              slug: result.slug,
              resultType: result.resultType,
              contentType: result.contentType,
            };

            // Add type-specific fields
            if (result.contentType === 'docs') {
              return {
                ...baseResult,
                category: this.highlightSearchTerm(searchQuery, result.category),
                categorySlug: result.categorySlug,
                priority: result.priority,
                groupCodes: result.groupCodes,
              };
            } else {
              // API result types
              return {
                ...baseResult,
                method: result.method,
                uri: this.highlightSearchTerm(searchQuery, result.uri),
                type: result.type,
                paramName: this.highlightSearchTerm(searchQuery, result.paramName),
                position: result.position,
                code: this.highlightSearchTerm(searchQuery, result.code),
                group: this.highlightSearchTerm(searchQuery, result.group),
                groupCode: result.groupCode,
                endpoint: result.endpoint,
                endpointPath: result.endpointPath,
                parent: result.parent,
                parentSlug: result.parentSlug,
                parentHasParent: result.parentHasParent,
                endpointHL: this.highlightSearchTerm(searchQuery, result.endpoint),
                parentHL: this.highlightSearchTerm(searchQuery, result.parent),
                paramNameHL: this.highlightSearchTerm(searchQuery, result.paramName),
              };
            }
          });

          this.resultsSourceSubject.next(results);
          this.loadingSource.next(false);

          if (updateFilterCounts) {
            this.setfiltersCounts(meta.typeFilters, meta.groupFilters);
          }
        } else {
          this.resultsSourceSubject.next([]);
          this.loadingSource.next(false);
        }
      });
  }

  /**
   * Creates a snippet from content showing context around search term matches.
   * Returns highlighted HTML with ellipses between non-contiguous matches.
   * 
   * @param term - the search term to highlight
   * @param content - the full content string to create snippet from
   * @returns highlighted HTML snippet
   */
  private createSearchSnippet(term: string, content: string): string {
    if (!term || !content) {
      return content ?? '';
    }

    const escaped = this.escapeRegExp(term);
    const regex = new RegExp(escaped, 'gi');
    const matches: { index: number; length: number }[] = [];

    // Find all match positions
    let match;
    while ((match = regex.exec(content)) !== null) {
      matches.push({ index: match.index, length: match[0].length });
    }

    // If no matches found, return truncated content
    if (matches.length === 0) {
      const maxLength = this.SNIPPET_CONTEXT_CHARS * 2;
      if (content.length <= maxLength) {
        return content;
      }
      return content.substring(0, maxLength).trim() + '...';
    }

    // Build snippets around each match, merging overlapping ranges
    const ranges: { start: number; end: number }[] = [];

    for (const m of matches.slice(0, this.MAX_SNIPPETS)) {
      const start = Math.max(0, m.index - this.SNIPPET_CONTEXT_CHARS);
      const end = Math.min(content.length, m.index + m.length + this.SNIPPET_CONTEXT_CHARS);

      // Check if this range overlaps with the previous one
      if (ranges.length > 0) {
        const lastRange = ranges[ranges.length - 1];
        if (start <= lastRange.end) {
          // Merge ranges
          lastRange.end = Math.max(lastRange.end, end);
          continue;
        }
      }

      ranges.push({ start, end });
    }

    // Extract and join snippets
    const snippets: string[] = [];

    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];
      let snippet = content.substring(range.start, range.end);

      // Trim to word boundaries for cleaner display
      if (range.start > 0) {
        // Find first space and trim from there
        const firstSpace = snippet.indexOf(' ');
        if (firstSpace > 0 && firstSpace < 15) {
          snippet = snippet.substring(firstSpace + 1);
        }
      }

      if (range.end < content.length) {
        // Find last space and trim to there
        const lastSpace = snippet.lastIndexOf(' ');
        if (lastSpace > snippet.length - 15 && lastSpace > 0) {
          snippet = snippet.substring(0, lastSpace);
        }
      }

      // Add leading ellipsis if not at start (only for first snippet)
      if (range.start > 0 && i == 0) {
        snippet = '...' + snippet;
      }

      // Add trailing ellipsis if not at end
      if (range.end < content.length) {
        snippet = snippet + '... ';
      }

      snippets.push(snippet);
    }

    // Join snippets and apply highlighting
    const joinedSnippet = snippets.join(' ');
    return this.highlightSearchTerm(term, joinedSnippet);
  }

  /**
   * 
   * Helper to escape special characters in a string for use in RegExp
   * 
   * @param s - the string to escape
   * @returns the escaped string
   */
  private escapeRegExp(s: string) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Highlights all occurrences of term in value by wrapping them in a span with class 'search-term'.
   * Case-insensitive.
   * 
   * @param term - the search term to highlight
   * @param value - the string value to search within
   * @returns the value with highlighted terms
   */
  private highlightSearchTerm(term: string, value: string) {
    if (!term || !value) return value ?? null;
    const escaped = this.escapeRegExp(term);
    const re = new RegExp(escaped, 'gi');
    return value.replace(re, (m) => `<span class="search-term">${m}</span>`);
  }

  /**
   * Getter method to fetch and set all group filters
   * 
   * @param currentVersion - the current API version
   */
  getGroups(currentDomainVersion: APIDomainVersion) {
    this.strapiService.getAllGroups(currentDomainVersion).subscribe((data) => {
      const groups = (data ?? []).map((r: any) => ({ name: r.Name, code: r.Code }));
      this.groupsSource.next(groups);
    });
  }

  /**
   * Setter method to set filter counts for type and group filters
   * 
   * @param typeFilterCounts - the counts for each type filter
   * @param groupFilterCounts - the counts for each group filter
   */
  setfiltersCounts(typeFilterCounts: TypeFilterCounts, groupFilterCounts: any) {
    let groupFiltersWithCount = [];
    let typeFiltersWithCount = [];
    let allGroups = this.groupsSource.getValue();

    for (const group of allGroups) {
      if (groupFilterCounts?.[group.code] !== undefined) {
        groupFiltersWithCount.push({
          name: group.name,
          code: group.code,
          count: groupFilterCounts[group.code],
        });
      }
    }

    this.groupFiltersWithCountSourceSubject.next(groupFiltersWithCount);

    for (const type of this.allTypeFilters) {
      if (typeFilterCounts?.[type] !== undefined) {
        typeFiltersWithCount.push({
          name: type,
          count: typeFilterCounts[type],
        });
      }
    }

    this.typeFiltersWithCountSourceSubject.next(typeFiltersWithCount);
  }

  /**
   * Clears all selected filters and resets filter counts
   */
  clearFilters() {
    this.selectedGroupFilterSourceSubject.next([]);
    this.selectedTypeFilterSourceSubject.next([]);
    this.groupFiltersWithCountSourceSubject.next([]);
    this.typeFiltersWithCountSourceSubject.next([]);
  }

  /**
   * Resets the search state completely
   */
  resetSearch() {
    this.clearFilters();
    this.resetPagination();
    this.setSearching(false);
    this.searchQuerySourceSubject.next('');
  }
}